/**
 * Editor de lugares (cidades do tempo, praças do agro).
 *
 * A lista é a ordem da tela: a primeira posição é o DESTAQUE (o contrato não tem
 * um campo separado para isso: ADR 0024, "a ordem do array é a ordem da tela").
 * Para incluir um município, a pessoa busca no catálogo do IBGE; o servidor
 * devolve slug, UF, código e coordenadas prontos, então nada é digitado à mão.
 */
import { useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import { useMunicipios } from '@/hooks/usePortal';
import type { ErroDeCampo, Local, Municipio } from '@/types/portal';

import { erroDe, mover, remover } from './helpers';
import styles from './Portal.module.css';
import { useAtrasado } from './useAtrasado';

interface Props {
  /** "Cidades" ou "Praças": entra nos rótulos e nas mensagens. */
  rotulo: string;
  /** "cidade" ou "praça". */
  singular: string;
  locais: Local[];
  onChange: (locais: Local[]) => void;
  erros: ErroDeCampo[];
  /** Caminho do campo no JSON, para casar o erro do servidor com esta lista. */
  campo: string;
}

export function EditorLocais({ rotulo, singular, locais, onChange, erros, campo }: Props) {
  const [termo, setTermo] = useState('');
  const buscado = useAtrasado(termo.trim(), 250);
  const busca = useMunicipios(buscado);
  const jaNaLista = new Set(locais.map((l) => l.ibge));
  const erroLista = erroDe(erros, campo);

  const adicionar = (m: Municipio) => {
    if (jaNaLista.has(m.ibge)) return;
    onChange([...locais, m]);
  };
  const destacar = (i: number) => onChange(mover(locais, i, 0));

  return (
    <div>
      <h4 className={styles.sub2}>
        {rotulo} ({locais.length})
      </h4>

      {locais.length === 0 ? (
        <p className={styles.vazio}>Nenhum município na lista. Busque abaixo e adicione.</p>
      ) : (
        <ol className={styles.lista} aria-label={rotulo}>
          {locais.map((l, i) => (
            <li key={l.ibge} className={i === 0 ? styles.destaque : styles.linha}>
              <div>
                <span className={styles.ordem}>{i + 1}</span>
                <span className={styles.linhaTitulo}>
                  {l.nome} ({l.uf})
                </span>{' '}
                {i === 0 && locais.length > 1 && (
                  <Badge tone="info" size="xs">
                    Destaque
                  </Badge>
                )}
                <div className={styles.linhaMeta}>
                  IBGE {l.ibge} · endereço {l.slug}
                </div>
              </div>
              <div className={styles.linhaAcoes}>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={i === 0}
                  onClick={() => destacar(i)}
                  aria-label={`Destacar ${l.nome}`}
                >
                  Destacar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={i === 0}
                  onClick={() => onChange(mover(locais, i, i - 1))}
                  aria-label={`Subir ${l.nome}`}
                >
                  Subir
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={i === locais.length - 1}
                  onClick={() => onChange(mover(locais, i, i + 1))}
                  aria-label={`Descer ${l.nome}`}
                >
                  Descer
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange(remover(locais, i))}
                  aria-label={`Remover ${l.nome}`}
                >
                  Remover
                </Button>
              </div>
            </li>
          ))}
        </ol>
      )}
      {erroLista && <span className={styles.erroCampo}>{erroLista}</span>}

      <div className={styles.busca}>
        <label className={styles.rotulo} htmlFor={`${campo}-busca`}>
          Buscar município no catálogo do IBGE
        </label>
        <input
          id={`${campo}-busca`}
          type="search"
          className={styles.entrada}
          value={termo}
          placeholder="Digite ao menos 2 letras do nome"
          onChange={(e) => setTermo(e.target.value)}
          autoComplete="off"
        />
        {buscado.length >= 2 && busca.isLoading && <span className={styles.dica}>Buscando…</span>}
        {busca.isError && (
          <span className={styles.erroCampo}>Não foi possível consultar o catálogo agora.</span>
        )}
        {buscado.length >= 2 && busca.data && busca.data.length === 0 && (
          <span className={styles.dica}>Nenhum município encontrado para "{buscado}".</span>
        )}
        {busca.data && busca.data.length > 0 && (
          <ul className={styles.resultados} aria-label="Resultados da busca">
            {busca.data.map((m) => {
              const incluido = jaNaLista.has(m.ibge);
              return (
                <li key={m.ibge} className={styles.resultado}>
                  <span>
                    {m.nome} ({m.uf}) <span className={styles.linhaMeta}>IBGE {m.ibge}</span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={incluido}
                    onClick={() => adicionar(m)}
                    aria-label={`Adicionar ${m.nome} (${m.uf})`}
                  >
                    {incluido ? 'Já na lista' : `Adicionar ${singular}`}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
